import type { JSX } from 'react';
import type { CreateTariffBody } from '@voxler/contracts';
import { assertNever } from '@voxler/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useToast } from '../../components/toast';
import { internalFetch, internalSend, validationDetail } from '../../lib/api';
import { datetimeLocalToIso, formatEffectiveFrom } from '../../lib/format';
import {
  CURRENCY_LABELS,
  CURRENCY_SYMBOLS,
  formatTariffPrice,
  TARIFF_MODE_LABELS,
  tariffInEffect,
  TARIFF_MODES,
  type Tariff,
  type TariffMode,
} from '../../lib/tariffs';
import type { Model, Provider } from '../../lib/use-cases';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { Field, Fields2, Fieldset, InputGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/** A tariff price as the API accepts it (`decimalString` of the contracts). */
const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;

/** Which prices a tariff of the provider carries: per SMS for Vonage, per token and image for AI. */
type TariffForm = 'sms' | 'ai';

function tariffFormOf(provider: Provider): TariffForm {
  switch (provider) {
    case 'vonage':
      return 'sms';
    case 'anthropic':
    case 'google':
      return 'ai';
    default:
      return assertNever(provider);
  }
}

type PriceField = Exclude<keyof CreateTariffBody, 'mode' | 'effectiveFrom' | 'currency'>;

/** Prices typed in each form, with their field label and column header; the others are sent as '0'. */
const PRICE_FIELDS: Record<
  TariffForm,
  readonly { field: PriceField; label: string; column: string }[]
> = {
  sms: [{ field: 'perMessage', label: 'Prix par SMS (segment)', column: 'Par SMS' }],
  ai: [
    { field: 'inputPerMTok', label: 'Entrée (par million de tokens)', column: 'Entrée /MTok' },
    { field: 'outputPerMTok', label: 'Sortie (par million de tokens)', column: 'Sortie /MTok' },
    {
      field: 'cacheWritePerMTok',
      label: 'Écriture de cache (par million de tokens)',
      column: 'Cache écrit /MTok',
    },
    {
      field: 'cacheReadPerMTok',
      label: 'Lecture de cache (par million de tokens)',
      column: 'Cache lu /MTok',
    },
    { field: 'perOutputImage', label: 'Par image générée', column: 'Image' },
  ],
};

const EMPTY_PRICES: Record<PriceField, string> = {
  inputPerMTok: '',
  outputPerMTok: '',
  cacheWritePerMTok: '',
  cacheReadPerMTok: '',
  perOutputImage: '',
  perMessage: '',
};

/** Where a form error is shown: under its field, or for the whole form (API refusal). */
type FormError = { target: PriceField | 'effectiveFrom' | 'form'; message: string } | null;

/** State of a tariff version against `now`: in effect, scheduled, or replaced by a later one. */
function TariffState({
  tariff,
  inEffect,
  now,
}: {
  tariff: Tariff;
  inEffect: ReadonlySet<string>;
  now: number;
}): JSX.Element {
  if (inEffect.has(tariff.id)) {
    return <Badge variant="success">En vigueur</Badge>;
  }
  if (new Date(tariff.effectiveFrom).getTime() > now) {
    return <Badge variant="info">À venir</Badge>;
  }
  return <Badge variant="neutral">Remplacé</Badge>;
}

/**
 * Tariff versions of a catalog entry (PRD 8.1, lot 17): the history and a form
 * to add a version. A tariff is never edited: a price change is a new version.
 * Mounted only while open, so its form starts empty each time.
 */
export function TariffsDialog({
  model,
  onClose,
}: {
  model: Model;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const form = tariffFormOf(model.provider);
  const fields = PRICE_FIELDS[form];

  const [mode, setMode] = useState<TariffMode>('standard');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [prices, setPrices] = useState<Record<PriceField, string>>(EMPTY_PRICES);
  const [formError, setFormError] = useState<FormError>(null);

  const tariffs = useQuery({
    queryKey: ['tariffs', model.id],
    queryFn: () => internalFetch<{ tariffs: Tariff[] }>(`/internal/models/${model.id}/tariffs`),
  });

  const addVersion = useMutation({
    mutationFn: (body: CreateTariffBody) =>
      internalSend<Tariff>('POST', `/internal/models/${model.id}/tariffs`, body),
    onSuccess: () => {
      setEffectiveFrom('');
      setPrices(EMPTY_PRICES);
      void queryClient.invalidateQueries({ queryKey: ['tariffs', model.id] });
      notify('Version de tarif ajoutée');
    },
    onError: (error) => {
      setFormError({
        target: 'form',
        message: validationDetail(error) ?? 'Échec de l’enregistrement de la version.',
      });
    },
  });

  const submit = (): void => {
    setFormError(null);
    const iso = datetimeLocalToIso(effectiveFrom);
    if (iso === null) {
      setFormError({ target: 'effectiveFrom', message: 'Date d’effet requise.' });
      return;
    }
    for (const { field, label } of fields) {
      if (!DECIMAL_PATTERN.test(prices[field].trim())) {
        setFormError({
          target: field,
          message: `${label} : nombre décimal attendu, point comme séparateur (ex. 0.08).`,
        });
        return;
      }
    }
    // A price the form does not show does not apply to this provider: '0'.
    const priceOf = (field: PriceField): string =>
      fields.some((entry) => entry.field === field) ? prices[field].trim() : '0';
    addVersion.mutate({
      // An SMS always leaves right away: a Vonage tariff is a standard one.
      mode: form === 'sms' ? 'standard' : mode,
      effectiveFrom: iso,
      // The entry's currency, checked by the API: a version never picks its own.
      currency: model.currency,
      inputPerMTok: priceOf('inputPerMTok'),
      outputPerMTok: priceOf('outputPerMTok'),
      cacheWritePerMTok: priceOf('cacheWritePerMTok'),
      cacheReadPerMTok: priceOf('cacheReadPerMTok'),
      perOutputImage: priceOf('perOutputImage'),
      perMessage: priceOf('perMessage'),
    });
  };

  const errorFor = (target: PriceField | 'effectiveFrom'): string | undefined =>
    formError !== null && formError.target === target ? formError.message : undefined;

  const now = Date.now();
  const list = [...(tariffs.data?.tariffs ?? [])].sort(
    (a, b) => new Date(a.effectiveFrom).getTime() - new Date(b.effectiveFrom).getTime(),
  );
  const inEffect = new Set<string>();
  for (const tariffMode of TARIFF_MODES) {
    const current = tariffInEffect(list, tariffMode, now);
    if (current !== undefined) {
      inEffect.add(current.id);
    }
  }
  const columns = fields.length + 4;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent width={form === 'sms' ? 640 : 900}>
        <DialogHead
          title={`Tarifs — ${model.displayName}`}
          description={`${
            form === 'sms'
              ? 'Prix d’achat Vonage par SMS (segment)'
              : 'Prix d’achat du fournisseur par million de tokens et par image'
          }, en ${CURRENCY_LABELS[model.currency]} (devise choisie à la création de l’entrée). Une nouvelle version remplace la précédente à sa date d’effet.`}
        />
        <form
          id="tariff-form"
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <DialogBody>
            <Table framed wrapperClassName="max-h-[280px]" aria-label="Versions de tarif">
              <TableHeader>
                <TableRow>
                  <TableHead>Mode</TableHead>
                  <TableHead>Date d’effet</TableHead>
                  <TableHead>Devise</TableHead>
                  {fields.map(({ field, column }) => (
                    <TableHead key={field} kind="num">
                      {column}
                    </TableHead>
                  ))}
                  <TableHead>État</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((tariff) => (
                  <TableRow key={tariff.id}>
                    <TableCell kind="name">{TARIFF_MODE_LABELS[tariff.mode]}</TableCell>
                    <TableCell kind="mono">{formatEffectiveFrom(tariff.effectiveFrom)}</TableCell>
                    <TableCell kind="mono">{tariff.currency}</TableCell>
                    {fields.map(({ field }) => (
                      <TableCell key={field} kind="num">
                        {formatTariffPrice(tariff[field], tariff.currency)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <TariffState tariff={tariff} inEffect={inEffect} now={now} />
                    </TableCell>
                  </TableRow>
                ))}
                {tariffs.isLoading ? (
                  <TableEmptyRow colSpan={columns}>Chargement…</TableEmptyRow>
                ) : null}
                {list.length === 0 && !tariffs.isLoading ? (
                  <TableEmptyRow colSpan={columns}>
                    Aucun tarif : toute requête sur ce {form === 'sms' ? 'service' : 'modèle'}{' '}
                    échouera tant qu’aucune version n’est en vigueur.
                  </TableEmptyRow>
                ) : null}
              </TableBody>
            </Table>

            <Fieldset
              title="Nouvelle version"
              description={`Prix en ${model.currency}, point comme séparateur décimal.`}
            >
              <Fields2>
                <Field label="Date d’effet" required error={errorFor('effectiveFrom')}>
                  <Input
                    type="datetime-local"
                    value={effectiveFrom}
                    onChange={(event) => {
                      setEffectiveFrom(event.target.value);
                    }}
                  />
                </Field>
                {form === 'ai' ? (
                  <Field label="Mode">
                    <NativeSelect
                      value={mode}
                      onChange={(event) => {
                        const next = TARIFF_MODES.find((option) => option === event.target.value);
                        if (next !== undefined) {
                          setMode(next);
                        }
                      }}
                    >
                      {TARIFF_MODES.map((option) => (
                        <option key={option} value={option}>
                          {TARIFF_MODE_LABELS[option]}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                ) : null}
              </Fields2>
              <Fields2>
                {fields.map(({ field, label }) => (
                  <Field key={field} label={label} required error={errorFor(field)}>
                    <InputGroup suffix={CURRENCY_SYMBOLS[model.currency]}>
                      <Input
                        numeric
                        value={prices[field]}
                        onChange={(event) => {
                          const value = event.target.value;
                          setPrices((current) => ({ ...current, [field]: value }));
                        }}
                        placeholder={form === 'sms' ? '0.08' : '0'}
                        inputMode="decimal"
                      />
                    </InputGroup>
                  </Field>
                ))}
              </Fields2>
            </Fieldset>

            {formError !== null && formError.target === 'form' ? (
              <Callout variant="destructive">{formError.message}</Callout>
            ) : null}
          </DialogBody>
        </form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Fermer
          </Button>
          <Button type="submit" form="tariff-form" size="lg" disabled={addVersion.isPending}>
            Ajouter la version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
